import type { AccountNewDropdownModel, ContextModel } from '@delfi-gui/components';

export interface User {
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  initials: string;
  /** Organisation / tenant shown during install and similar flows. */
  corporateAccount: string;
  countryIso: string;
}

export interface UserAccount {
  /** Display name, e.g. "Digital | Foundation | Delfi Deployment" */
  name: string;
  /** Billing account ID, e.g. "2RW1TRP86S" */
  billingCrmAccountId: string;
  /** True when the signed-in user is an account manager (admin) for this account. */
  isAccountManager: boolean;
}

export interface UserMissingData {
  missingCompanyName: boolean;
  missingCountry: boolean;
  missingFullName: boolean;
}

export interface SapUserResponse {
  firstName?: string;
  lastName?: string;
  uid?: string;
  orgUnit?: { name?: string };
  countryIso?: string;
  country?: { isocode?: string; name?: string };
  missingData?: UserMissingData;
  completedRegistration?: boolean;
  sauth_access_token?: string;
}

export function mapSapUser(response: SapUserResponse): User {
  const firstName = response.firstName ?? '';
  const lastName = response.lastName ?? '';
  return {
    firstName,
    lastName,
    name: `${firstName} ${lastName}`.trim(),
    email: response.uid ?? '',
    initials: `${firstName[0] ?? ''}${lastName[0] ?? ''}`,
    corporateAccount: response.orgUnit?.name ?? '',
    countryIso: response.country?.isocode ?? response.countryIso ?? '',
  };
}

const SLB_DOMAINS = new Set(['slb.com']);

export function getUserType(email: string): 'slb' | 'external' {
  const domain = email.split('@').pop()?.toLowerCase() ?? '';
  return SLB_DOMAINS.has(domain) ? 'slb' : 'external';
}

/** Extracts a sorted, deduplicated contract list from a single account's departments. */
export function extractAccountContracts(acct: AccountNewDropdownModel): { id: string; name: string }[] {
  const seen = new Set<string>();
  return (acct.departments ?? [])
    .flatMap(d => d.contracts ?? [])
    .map(c => ({ id: c.crmContractId, name: c.crmContractName ?? c.crmContractId }))
    .filter(c => seen.has(c.id) ? false : (seen.add(c.id), true))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function mapContextToUserAccount(ctx: ContextModel): UserAccount {
  return {
    name: ctx.accountName,
    billingCrmAccountId: ctx.crmAccountId,
    isAccountManager: ctx.user?.accountManager ?? false,
  };
}

export type InstallStatus = 'not_installed' | 'sandbox' | 'installed' | 'entitled';
